import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

const ROLE_LABELS = { main: "Main", support: "Support", "sub-support": "Sub-support" };
const ROLE_COLORS = { main: "#ef4444", support: "#f59e0b", "sub-support": "#6366f1" };

export default function InterlinkingView() {
  const { proj, activeId, dark, t, card, inp, btnP, btnG, btnSm, flash, apiFetch } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("structure");
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedWhat, setCopiedWhat] = useState(null);
  const [style, setStyle] = useState({ bg: "", font: "", line: "", chipBg: "", textBg: "", textColor: "", textAccent: "" });
  const [layout, setLayout] = useState("default");
  const [savingStyle, setSavingStyle] = useState(false);
  const token = localStorage.getItem("metago_token");

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    apiFetch(`/api/projects/${activeId}/interlinking/footer`)
      .then(r => r.json())
      .then(d => { if (d && d.pages) setData(d); if (d && d.style) setStyle({ bg: d.style.bg || "", font: d.style.font || "", line: d.style.line || "", chipBg: d.style.chipBg || "", textBg: d.style.textBg || "", textColor: d.style.textColor || "", textAccent: d.style.textAccent || "" }); if (d && d.layout) setLayout(d.layout); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeId, apiFetch]);

  const generate = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await fetch(`/api/projects/${activeId}/interlinking/footer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (d.error) flash(d.error); else { setData(d); setDirty(false); }
    } catch (e) { flash("Помилка: " + e.message); }
    setLoading(false);
  };

  const saveEdits = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await fetch(`/api/projects/${activeId}/interlinking/footer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pages: data.pages }),
      });
      flash("Збережено");
      setDirty(false);
    } catch { flash("Помилка збереження"); }
    setSaving(false);
  };

  const saveStyle = async () => {
    setSavingStyle(true);
    try {
      const r = await fetch(`/api/projects/${activeId}/interlinking/style`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...style, layout }),
      });
      const d = await r.json();
      if (d.error) flash(d.error); else flash("Стиль збережено (застосується на сайті за ~хвилину)");
    } catch (e) { flash("Помилка: " + e.message); }
    setSavingStyle(false);
  };

  const updatePageField = (url, field, value) => {
    setData(prev => ({
      ...prev,
      pages: prev.pages.map(p => p.url === url ? { ...p, [field]: value } : p),
    }));
    setDirty(true);
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedWhat(label);
    setTimeout(() => setCopiedWhat(null), 2000);
    flash(`${label} скопійовано`);
  };

  const stripDomain = (url) => { try { return new URL(url).pathname; } catch { return url; } };

  // Page detail by selected
  const selPage = useMemo(() => data?.pages?.find(p => p.url === selectedPage), [data, selectedPage]);

  if (!proj) return null;

  const tabStyle = (active) => ({
    padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none",
    borderBottom: active ? "2px solid #6366f1" : "2px solid transparent",
    background: "none", color: active ? "#6366f1" : t.textMuted, fontFamily: "inherit",
  });

  const roleBadge = (role) => (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
      background: `${ROLE_COLORS[role] || "#64748b"}18`,
      color: ROLE_COLORS[role] || "#64748b",
      border: `1px solid ${ROLE_COLORS[role] || "#64748b"}30`,
      textTransform: "uppercase", letterSpacing: 0.5,
    }}>{ROLE_LABELS[role] || role}</span>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>SEO кокон</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {dirty && (
            <button onClick={saveEdits} disabled={saving} style={{ ...btnG, fontSize: 12, opacity: saving ? 0.6 : 1 }}>
              {saving ? "Зберігаю..." : "Зберегти зміни"}
            </button>
          )}
          <button onClick={generate} disabled={loading} style={{ ...btnP, fontSize: 12, opacity: loading ? 0.6 : 1 }}>
            {loading ? "Генерую..." : data ? "Перегенерувати" : "Згенерувати"}
          </button>
        </div>
      </div>

      {!data && !loading && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🕸️</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>SEO-кокон перелінковки</p>
          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, maxWidth: 500, margin: "0 auto 16px" }}>
            Система внутрішньої перелінковки з ролями (Main / Support / Sub-support),
            тематичними кластерами і двома блоками посилань.
          </p>
          <button onClick={generate} disabled={loading} style={btnP}>Згенерувати</button>
        </div>
      )}

      {loading && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 14, color: t.textMuted }}>Аналіз кластерів і генерація кокону...</div>
        </div>
      )}

      {data && (
        <>
          {/* Stats */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            {[
              { label: "Всього", value: data.stats.totalPages, color: "#6366f1" },
              { label: "Main", value: data.stats.mainPages, color: "#ef4444" },
              { label: "Support", value: data.stats.supportPages, color: "#f59e0b" },
              { label: "Sub-support", value: data.stats.subSupportPages, color: "#6366f1" },
              { label: "Кластерів", value: data.stats.clusterCount, color: "#8b5cf6" },
            ].map((s, i) => (
              <div key={i} style={{ ...card, marginBottom: 0, padding: 14, minWidth: 80, textAlign: "center", flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Cocoon block colors */}
          <div style={{ ...card, marginBottom: 14, padding: 14, display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>🎨 Стиль блоку на сайті</span>
            {[
              { key: "bg", label: "Фон", def: "#ffffff" },
              { key: "font", label: "Шрифт", def: "#1a1a22" },
              { key: "line", label: "Лінії", def: "#e6e6ec" },
              { key: "chipBg", label: "Кнопки", def: "#f6f6fa" },
              { key: "textBg", label: "Блок тексту (фон)", def: "#1f1830" },
              { key: "textColor", label: "Блок тексту (текст)", def: "#ffffff" },
              { key: "textAccent", label: "Блок тексту (акцент)", def: "#a855f7" },
            ].map(c => (
              <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: t.textMuted }}>
                {c.label}
                <input type="color" value={style[c.key] || c.def} onChange={e => setStyle(st => ({ ...st, [c.key]: e.target.value }))}
                  style={{ width: 34, height: 28, padding: 0, border: `1px solid ${t.border}`, borderRadius: 6, background: "transparent", cursor: "pointer" }} />
                {style[c.key] && <button onClick={() => setStyle(st => ({ ...st, [c.key]: "" }))} title="Скинути до типового" style={{ ...btnSm, fontSize: 10, padding: "2px 6px" }}>✕</button>}
              </label>
            ))}
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: t.textMuted }}>Верстка
              <select value={layout} onChange={e => setLayout(e.target.value)} style={{ fontSize: 12, padding: "5px 8px", border: `1px solid ${t.border}`, borderRadius: 6, background: "transparent", color: "inherit", cursor: "pointer" }}>
                <option value="default">Стандартна</option>
                <option value="readmore">Альтернативна «Читати більше»</option>
              </select>
            </label>
            <button onClick={saveStyle} disabled={savingStyle} style={{ ...btnP, fontSize: 12, marginLeft: "auto", opacity: savingStyle ? 0.6 : 1 }}>
              {savingStyle ? "Зберігаю..." : "Зберегти стиль"}
            </button>
            <span style={{ fontSize: 11, color: t.textMuted, width: "100%" }}>Порожній колір = типовий. Застосується на сайті за ~хвилину (кеш SDK).</span>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: `1px solid ${t.border}`, marginBottom: 14 }}>
            <button style={tabStyle(tab === "structure")} onClick={() => setTab("structure")}>Структура</button>
            <button style={tabStyle(tab === "preview")} onClick={() => setTab("preview")}>Превью блоків</button>
            <button style={tabStyle(tab === "pages")} onClick={() => setTab("pages")}>Сторінки ({data.pages.length})</button>
            <button style={tabStyle(tab === "install")} onClick={() => setTab("install")}>Встановлення</button>
          </div>

          {/* ─── Structure: clusters with roles ─── */}
          {tab === "structure" && (
            <div>
              {(data.clusters || []).map((cl, ci) => {
                const isOpen = expandedCluster === ci;
                const clPages = data.pages.filter(p => p.cluster === cl.id);
                const mains = clPages.filter(p => p.role === "main");
                const supports = clPages.filter(p => p.role === "support");
                const subs = clPages.filter(p => p.role === "sub-support");

                return (
                  <div key={ci} style={{ ...card, padding: 0, marginBottom: 8, overflow: "hidden" }}>
                    <div onClick={() => setExpandedCluster(isOpen ? null : ci)} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer",
                      background: isOpen ? (dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.03)") : "transparent",
                    }}>
                      <span style={{ fontSize: 10, color: isOpen ? "#6366f1" : t.textMuted, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .2s" }}>▶</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{cl.name}</div>
                        <div style={{ fontSize: 11, color: t.textMuted }}>
                          {cl.pages.length} стор. — {cl.mainCount} Main, {cl.supportCount} Support, {cl.subSupportCount} Sub-support
                        </div>
                      </div>
                    </div>
                    {isOpen && (
                      <div style={{ padding: "0 16px 12px" }}>
                        {/* Main pages */}
                        {mains.length > 0 && (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", marginBottom: 4, textTransform: "uppercase" }}>Main (Money)</div>
                            {mains.map((p, i) => (
                              <PageRow key={i} page={p} dark={dark} t={t} stripDomain={stripDomain} roleBadge={roleBadge}
                                onClick={() => setSelectedPage(selectedPage === p.url ? null : p.url)} selected={selectedPage === p.url}
                                onRoleChange={(role) => updatePageField(p.url, "role", role)} />
                            ))}
                          </div>
                        )}
                        {/* Support pages */}
                        {supports.length > 0 && (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#f59e0b", marginBottom: 4, textTransform: "uppercase" }}>Support</div>
                            {supports.map((p, i) => (
                              <PageRow key={i} page={p} dark={dark} t={t} stripDomain={stripDomain} roleBadge={roleBadge}
                                onClick={() => setSelectedPage(selectedPage === p.url ? null : p.url)} selected={selectedPage === p.url}
                                onRoleChange={(role) => updatePageField(p.url, "role", role)} />
                            ))}
                          </div>
                        )}
                        {/* Sub-support pages */}
                        {subs.length > 0 && (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", marginBottom: 4, textTransform: "uppercase" }}>Sub-support</div>
                            {subs.map((p, i) => (
                              <PageRow key={i} page={p} dark={dark} t={t} stripDomain={stripDomain} roleBadge={roleBadge}
                                onClick={() => setSelectedPage(selectedPage === p.url ? null : p.url)} selected={selectedPage === p.url}
                                onRoleChange={(role) => updatePageField(p.url, "role", role)} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Selected page detail panel */}
              {selPage && (
                <div style={{ ...card, padding: 16, marginTop: 8, border: `2px solid ${ROLE_COLORS[selPage.role] || "#6366f1"}40` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    {roleBadge(selPage.role)}
                    <span style={{ fontSize: 13, fontWeight: 700, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{stripDomain(selPage.url)}</span>
                    <button onClick={() => setSelectedPage(null)} style={{ ...btnSm, fontSize: 10 }}>✕</button>
                  </div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>Анкор: <strong style={{ color: "#6366f1" }}>{selPage.anchor}</strong></div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 8 }}>Всі анкори: {selPage.anchors.slice(0, 8).join(" | ")}</div>

                  {(selPage.block1 || []).length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Блок 1: Пов'язані послуги</div>
                      {(selPage.block1 || []).map((l, i) => {
                        const target = data.pages.find(p => p.url === l.url);
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", fontSize: 11 }}>
                            <span style={{ color: "#6366f1", fontWeight: 500 }}>{l.anchor}</span>
                            <span style={{ color: t.textMuted }}>→</span>
                            <span style={{ color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{stripDomain(l.url)}</span>
                            {target && roleBadge(target.role)}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {(selPage.block2 || []).length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Блок 2: Популярні рішення</div>
                      {(selPage.block2 || []).map((l, i) => {
                        const target = data.pages.find(p => p.url === l.url);
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", fontSize: 11 }}>
                            <span style={{ color: "#f59e0b", fontWeight: 500 }}>{l.anchor}</span>
                            <span style={{ color: t.textMuted }}>→</span>
                            <span style={{ color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{stripDomain(l.url)}</span>
                            {target && roleBadge(target.role)}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {(selPage.block3 || []).length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Блок 3: Тематичні зв'язки <span style={{ fontWeight: 400, color: t.textMuted, fontSize: 10 }}>(на основі n-грам)</span></div>
                      {(selPage.block3 || []).map((l, i) => {
                        const target = data.pages.find(p => p.url === l.url);
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", fontSize: 11 }}>
                            <span style={{ color: "#22c55e", fontWeight: 500 }}>{l.anchor}</span>
                            <span style={{ color: t.textMuted }}>→</span>
                            <span style={{ color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{stripDomain(l.url)}</span>
                            {target && roleBadge(target.role)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ─── Preview: how blocks look on site ─── */}
          {tab === "preview" && (() => {
            const previewPage =
              selPage ||
              data.pages.find(p => p.role === "support" && ((p.block1 || []).length || (p.block2 || []).length || (p.block3 || []).length)) ||
              data.pages.find(p => (p.block1 || []).length || (p.block2 || []).length || (p.block3 || []).length) ||
              data.pages[0];

            if (!previewPage) {
              return (
                <div style={{ ...card, textAlign: "center", padding: 32 }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>Немає сторінок для превью</p>
                  <p style={{ fontSize: 12, color: t.textMuted }}>Перегенеруйте кокон, щоб отримати дані.</p>
                </div>
              );
            }

            const b1 = previewPage.block1 || [];
            const b2 = previewPage.block2 || [];
            const b3 = previewPage.block3 || [];
            const hasAnyBlock = b1.length + b2.length + b3.length > 0;
            const roleLabel = ROLE_LABELS[previewPage.role] || "—";

            return (
              <div>
                <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 8 }}>
                  Превью для: <strong>{stripDomain(previewPage.url)}</strong> ({roleLabel})
                  {!selPage && <span> — натисніть на сторінку в "Структура" щоб переглянути іншу</span>}
                </div>

                {!hasAnyBlock && (
                  <div style={{ ...card, textAlign: "center", padding: 32 }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>🧩</div>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>Для цієї сторінки блоків ще немає</p>
                    <p style={{ fontSize: 12, color: t.textMuted, maxWidth: 460, margin: "0 auto" }}>
                      {previewPage.role
                        ? "Усі три блоки порожні — спробуйте іншу сторінку в табі «Структура» або перегенеруйте кокон."
                        : "Структура кокону застаріла (немає ролей і блоків). Натисніть «Перегенерувати» вгорі, щоб оновити."}
                    </p>
                  </div>
                )}

                {/* Block 1 */}
                {b1.length > 0 && (
                  <div style={{ ...card, padding: "20px 24px", borderRadius: 14, marginBottom: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Пов'язані послуги</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6 }}>
                      {b1.map((l, i) => (
                        <div key={i} style={{
                          padding: "10px 14px", borderRadius: 10,
                          background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                          border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                          fontSize: 13, fontWeight: 500, color: t.text,
                        }}>{l.anchor}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Block 2 */}
                {b2.length > 0 && (
                  <div style={{ ...card, padding: "20px 24px", borderRadius: 14, marginBottom: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Популярні рішення</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6 }}>
                      {b2.map((l, i) => (
                        <div key={i} style={{
                          padding: "10px 14px", borderRadius: 10,
                          background: dark ? "rgba(239,68,68,0.05)" : "rgba(239,68,68,0.03)",
                          border: `1px solid ${dark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)"}`,
                          fontSize: 13, fontWeight: 500, color: t.text,
                        }}>{l.anchor}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Block 3 — n-grams */}
                {b3.length > 0 && (
                  <div style={{ ...card, padding: "20px 24px", borderRadius: 14 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Тематичні зв'язки</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6 }}>
                      {b3.map((l, i) => (
                        <div key={i} style={{
                          padding: "10px 14px", borderRadius: 10,
                          background: dark ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.03)",
                          border: `1px solid ${dark ? "rgba(34,197,94,0.14)" : "rgba(34,197,94,0.10)"}`,
                          fontSize: 13, fontWeight: 500, color: t.text,
                        }}>{l.anchor}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ─── Pages table ─── */}
          {tab === "pages" && (
            <div style={{ ...card, padding: 0, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["URL", "Роль", "Кластер", "Анкор", "Freq", "Блок 1", "Блок 2", "Блок 3"].map(h => (
                      <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.textMuted, borderBottom: `2px solid ${t.border}`, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.pages.map((p, i) => (
                    <tr key={i} style={{
                      background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)"),
                      cursor: "pointer",
                    }} onClick={() => { setSelectedPage(p.url); setTab("structure"); }}>
                      <td style={{ padding: "6px 10px", fontSize: 11, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.url}>{stripDomain(p.url)}</td>
                      <td style={{ padding: "6px 10px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>{roleBadge(p.role)}</td>
                      <td style={{ padding: "6px 10px", fontSize: 11, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, color: t.textMuted }}>{p.clusterName}</td>
                      <td style={{ padding: "6px 10px", fontSize: 12, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, color: "#6366f1", fontWeight: 500 }}>{p.anchor}</td>
                      <td style={{ padding: "6px 10px", fontSize: 12, fontWeight: 700, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>{p.freq}</td>
                      <td style={{ padding: "6px 10px", fontSize: 11, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, color: "#10b981" }}>{(p.block1 || []).length}</td>
                      <td style={{ padding: "6px 10px", fontSize: 11, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, color: "#f59e0b" }}>{(p.block2 || []).length}</td>
                      <td style={{ padding: "6px 10px", fontSize: 11, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`, color: "#22c55e" }}>{(p.block3 || []).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ─── Install ─── */}
          {tab === "install" && (
            <div>
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "12px 18px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>HTML код для сайту</span>
                  <span style={{ fontSize: 10, color: t.textMuted }}>Вставити перед &lt;/body&gt;</span>
                  <button onClick={() => copy(data.widgetScript, "Widget")} style={{ ...btnP, fontSize: 11, padding: "5px 14px", marginLeft: "auto" }}>
                    {copiedWhat === "Widget" ? "✓" : "Копіювати"}
                  </button>
                </div>
                <pre style={{ padding: "12px 18px", margin: 0, fontSize: 10, lineHeight: 1.4, background: dark ? "#0a0a10" : "#f8f9fb", color: dark ? "#8b8ba0" : "#555", overflow: "auto", maxHeight: 350, whiteSpace: "pre-wrap" }}>
                  {data.widgetScript}
                </pre>
              </div>

              <div style={{ ...card, marginTop: 12, padding: "14px 18px" }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Як працює</div>
                <div style={{ fontSize: 12, color: t.text, lineHeight: 1.8 }}>
                  {[
                    "На кожній сторінці автоматично виводяться ДВА блоки посилань",
                    "\"Пов'язані послуги\" — сторінки з того ж тематичного кластера (3-6 шт.)",
                    "\"Популярні рішення\" — Main (грошові) сторінки для підсилення (тільки на Support/Sub-support)",
                    "\"Тематичні зв'язки\" — до 6 лінків з анкорами-n-грамами на сторінки де ці n-грами найчастотніші (потребує запущеного N-gram аналізу)",
                    "Анкори = ключові слова, унікальні для кожної сторінки",
                    "Support → обов'язково посилається на Main",
                    "Sub-support → посилається на Support + Main",
                    "Main → тільки на релевантні Support (3-5 шт.)",
                    "HTML блоки доступні для індексації пошуковими системами",
                  ].map((step, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: 11, color: "#6366f1" }}>•</span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              {data.apiUrl && (
                <div style={{ ...card, marginTop: 12, padding: "14px 18px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#10b981", marginBottom: 6 }}>API Endpoint</div>
                  <code style={{ fontSize: 11, wordBreak: "break-all", color: t.text, background: dark ? "#0a0a10" : "#f8f9fb", padding: "6px 10px", borderRadius: 6, display: "block" }}>{data.apiUrl}</code>
                  <div style={{ fontSize: 10, color: t.textMuted, marginTop: 6 }}>Публічний, кеш 24 год.</div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Page row component ───────────────────────────────────────────────
function PageRow({ page, dark, t, stripDomain, roleBadge, onClick, selected, onRoleChange }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, cursor: "pointer",
        background: selected ? (dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.04)") : "transparent",
        borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}`,
      }}
    >
      <span style={{ fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={page.url}>
        {stripDomain(page.url)}
      </span>
      <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 500, flexShrink: 0, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {page.anchor}
      </span>
      <span style={{ fontSize: 10, color: t.textMuted, flexShrink: 0 }}>f:{page.freq}</span>
      <span style={{ fontSize: 10, color: "#10b981", flexShrink: 0 }}>B1:{(page.block1 || []).length}</span>
      <span style={{ fontSize: 10, color: "#f59e0b", flexShrink: 0 }}>B2:{(page.block2 || []).length}</span>
      <span style={{ fontSize: 10, color: "#22c55e", flexShrink: 0 }}>B3:{(page.block3 || []).length}</span>
      {/* Role selector */}
      <select
        value={page.role}
        onChange={e => { e.stopPropagation(); onRoleChange(e.target.value); }}
        onClick={e => e.stopPropagation()}
        style={{
          fontSize: 10, padding: "2px 4px", borderRadius: 4, border: `1px solid ${t.border}`,
          background: dark ? "#1a1a2e" : "#fff", color: ROLE_COLORS[page.role] || t.text,
          fontWeight: 700, cursor: "pointer",
        }}
      >
        <option value="main">Main</option>
        <option value="support">Support</option>
        <option value="sub-support">Sub-support</option>
      </select>
    </div>
  );
}
